
import styles from './StaffActivityCard.module.css';
import { StaffInfo } from '../../Networking/Interface/Management/StaffActivityInterfaces';
import InfoLineView, { InfoLine } from '../../Components/InfoLineView';
import UserRoleBadge from '../../Components/UserRoleBadge';

interface StaffActivityCardProps {
    staffInfo: StaffInfo;
}

export const StaffActivityCard: React.FC<StaffActivityCardProps> = ({ staffInfo }) => {
    const { username, userRole, pfp, staffActivitySummary, staffPunishmentSummary, staffTicketSummary } = staffInfo;
    const profilePicture = pfp ? `${pfp}` : `${process.env.PUBLIC_URL}/Staff/staff-default.png`;
    const { label, color } = formatDateDifference(staffActivitySummary?.lastOnline);

    return (
        <div className={styles.staffCard}>
            <div className={styles.lastOnlineBadge} style={{ backgroundColor: color }}>
                {label}
            </div>
            <div className={styles.profileSection}>
                <div className={styles.profilePicWrapper}>
                    <img src={profilePicture} alt="Staff Profile" className={styles.profilePic} />
                </div>
                <h3 className={styles.staffName}>{username || 'Unknown Staff'}</h3>
                <UserRoleBadge role={userRole} />
            </div>

            <div className={styles.activitySection}>
                <div className={styles.activityItem}>
                    <img src={`${process.env.PUBLIC_URL}/Staff/warn.png`} alt="Warnings" className={styles.icon} />
                    <span>{staffPunishmentSummary?.totalWarns ?? 0}</span>
                </div>
                <div className={styles.activityItem}>
                    <img src={`${process.env.PUBLIC_URL}/Staff/jail.png`} alt="AJails" className={styles.icon} />
                    <span>{staffPunishmentSummary?.totalAJails ?? 0}</span>
                </div>
                <div className={styles.activityItem}>
                    <img src={`${process.env.PUBLIC_URL}/Staff/kick.png`} alt="Kicks" className={styles.icon} />
                    <span>{staffPunishmentSummary?.totalKicks ?? 0}</span>
                </div>
                <div className={styles.activityItem}>
                    <img src={`${process.env.PUBLIC_URL}/Staff/mute.png`} alt="Mutes" className={styles.icon} />
                    <span>{staffPunishmentSummary?.totalMutes ?? 0}</span>
                </div>
                <div className={styles.activityItem}>
                    <img src={`${process.env.PUBLIC_URL}/Staff/ban.png`} alt="Bans" className={styles.icon} />
                    <span>{staffPunishmentSummary?.totalBans ?? 0}</span>
                </div>
            </div>

            <div className={styles.ticketSection}>
                <InfoLineView title="Tickets">
                    <InfoLine label="Total Handled" value={staffTicketSummary?.totalHandledTickets ?? 0} />
                    <InfoLine label="Last 7 days" value={staffTicketSummary?.ticketsLast7Days ?? 0} />
                    <InfoLine label="Avg. Time" value={`${staffTicketSummary?.averageHandlingTime?.toFixed(1) ?? '—'} min`} />
                </InfoLineView>
            </div>
        </div>
    );
};

interface DateDifference {
    label: string;
    color: string;
}

const formatDateDifference = (lastOnline: Date | null | undefined): DateDifference => {
    if (!lastOnline) return { label: "Retired", color: "#808080" };

    const lastOnlineDate = new Date(lastOnline);
    const now = new Date();
    const differenceInMs = now.getTime() - lastOnlineDate.getTime();
    const differenceInDays = differenceInMs / (1000 * 3600 * 24);

    let label: string;
    let color: string = "#333";

    if (differenceInDays < 1) {
        label = "Today";
        color = "#2d8a4e";
    } else if (differenceInDays < 3) {
        label = `${Math.floor(differenceInDays)}d ago`;
    } else if (differenceInDays < 7) {
        label = `${Math.floor(differenceInDays)}d ago`;
        color = "#b8860b";
    } else {
        label = `${Math.floor(differenceInDays / 7)}w ago`;
        color = "#8B0000";
    }

    return { label, color };
};
